import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  normalizeDate,
  formatDateString,
  getWeekStart,
  getWeekDatesArray,
  getWeekRangeText,
  getTodayString,
} from '../utils/dateUtils';

/**
 * JumpToPicker
 *
 * A portal-rendered modal that lets the user jump to a specific date, week,
 * or month. Designed to be reused by the Plan and Progress pages.
 *
 * Props:
 * - isOpen: boolean
 * - mode: 'date' | 'week' | 'month'
 * - selectedDate: YYYY-MM-DD string (highlights this date in date mode)
 * - weekOffset: number (highlights this week in week mode)
 * - monthOffset: number (highlights this month in month mode)
 * - onSelectDate: (dateStr) => void          -- called for mode='date'
 * - onSelectWeek: (weekOffset) => void       -- called for mode='week'
 * - onSelectMonth: (monthOffset) => void     -- called for mode='month'
 * - onClose: () => void
 * - pastRange: number (months/weeks backwards from today, default 12)
 * - futureRange: number (months/weeks forward from today, default 12)
 *
 * Rendered via createPortal to document.body so iOS WebView stacking
 * issues from sticky/transformed ancestors cannot hide the modal.
 */
function JumpToPicker({
  isOpen,
  mode = 'date',
  selectedDate,
  weekOffset = 0,
  monthOffset = 0,
  onSelectDate,
  onSelectWeek,
  onSelectMonth,
  onClose,
  pastRange = 12,
  futureRange = 12,
}) {
  var today = getTodayString();
  var todayDate = normalizeDate(today);

  // Date-mode state: which month is being displayed in the calendar
  var initialPickerMonth = selectedDate
    ? normalizeDate(selectedDate).getMonth()
    : todayDate.getMonth();
  var initialPickerYear = selectedDate
    ? normalizeDate(selectedDate).getFullYear()
    : todayDate.getFullYear();

  var [pickerMonth, setPickerMonth] = useState(initialPickerMonth);
  var [pickerYear, setPickerYear] = useState(initialPickerYear);

  // Reset internal month/year whenever the picker is reopened
  useEffect(function () {
    if (isOpen && mode === 'date') {
      var d = selectedDate ? normalizeDate(selectedDate) : todayDate;
      setPickerMonth(d.getMonth());
      setPickerYear(d.getFullYear());
    }
  }, [isOpen, mode, selectedDate]);

  // Scroll the highlighted row into view when week/month picker opens
  var listRef = useRef(null);
  useEffect(function () {
    if (!isOpen) return;
    if (mode !== 'week' && mode !== 'month') return;
    var node = listRef.current;
    if (!node) return;
    var highlighted = node.querySelector('[data-current="true"]');
    if (highlighted && typeof highlighted.scrollIntoView === 'function') {
      highlighted.scrollIntoView({ block: 'center' });
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  // -- Date mode helpers ----------------------------------------------------
  var monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  var monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function buildCalendarDays() {
    var daysInMonth = new Date(pickerYear, pickerMonth + 1, 0).getDate();
    var firstDayOfWeek = new Date(pickerYear, pickerMonth, 1).getDay();
    var days = [];
    for (var i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (var d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }

  function shiftMonth(delta) {
    var m = pickerMonth + delta;
    var y = pickerYear;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setPickerMonth(m);
    setPickerYear(y);
  }

  // -- Week mode helpers ----------------------------------------------------
  function formatShortDate(d) {
    var mm = d.getMonth() + 1;
    var dd = d.getDate();
    var yy = String(d.getFullYear()).slice(-2);
    return mm + '/' + dd + '/' + yy;
  }

  function buildWeekList() {
    var weeks = [];
    var todayWeekStart = getWeekStart(today);
    for (var off = -pastRange; off <= futureRange; off++) {
      var start = new Date(todayWeekStart);
      start.setDate(todayWeekStart.getDate() + off * 7);
      var end = new Date(start);
      end.setDate(start.getDate() + 6);
      weeks.push({
        offset: off,
        label: formatShortDate(start) + ' - ' + formatShortDate(end),
        isCurrent: off === 0,
      });
    }
    return weeks;
  }

  // -- Month mode helpers ---------------------------------------------------
  function buildMonthList() {
    var months = [];
    var now = new Date();
    var baseMonth = now.getMonth();
    var baseYear = now.getFullYear();
    for (var off = -pastRange; off <= futureRange; off++) {
      var m = baseMonth + off;
      var y = baseYear;
      while (m < 0) { m += 12; y -= 1; }
      while (m > 11) { m -= 12; y += 1; }
      months.push({
        offset: off,
        label: monthNames[m] + ' ' + y,
        isCurrent: off === 0,
      });
    }
    return months;
  }

  // -- Render ---------------------------------------------------------------
  var content;
  if (mode === 'date') {
    var days = buildCalendarDays();
    content = (
      <div data-testid="jump-picker-date">
        {/* Month header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={function () { shiftMonth(-1); }}
            className="p-1.5 rounded-full hover:bg-secondary"
            data-testid="jump-picker-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-semibold text-sm">
            {monthAbbr[pickerMonth]} {pickerYear}
          </span>
          <button
            onClick={function () { shiftMonth(1); }}
            className="p-1.5 rounded-full hover:bg-secondary"
            data-testid="jump-picker-next-month"
            aria-label="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(function (d, i) {
            return (
              <div key={i} className="text-center text-[10px] text-muted-foreground font-medium py-1">
                {d}
              </div>
            );
          })}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map(function (day, idx) {
            if (!day) {
              return <div key={'empty-' + idx} className="py-2" />;
            }
            var dateStr = pickerYear + '-' + String(pickerMonth + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
            var isToday = dateStr === today;
            var isSelected = dateStr === selectedDate;
            return (
              <button
                key={dateStr}
                onClick={function () {
                  if (onSelectDate) onSelectDate(dateStr);
                  onClose && onClose();
                }}
                data-testid={'jump-picker-day-' + dateStr}
                className={
                  'py-2 text-xs rounded-lg transition-all ' +
                  (isSelected
                    ? 'bg-accent text-white'
                    : isToday
                      ? 'bg-accent/20 text-accent font-medium'
                      : 'hover:bg-secondary')
                }
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Quick actions */}
        <div className="mt-4 pt-3 border-t border-border/40 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs h-8"
            onClick={function () {
              if (onSelectDate) onSelectDate(today);
              onClose && onClose();
            }}
            data-testid="jump-picker-today"
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8"
            onClick={function () { onClose && onClose(); }}
            data-testid="jump-picker-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  } else if (mode === 'week') {
    var weeks = buildWeekList();
    content = (
      <div data-testid="jump-picker-week">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-sm">Jump to week</span>
          <button
            onClick={function () { onClose && onClose(); }}
            className="p-1 rounded-full hover:bg-secondary"
            data-testid="jump-picker-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div
          ref={listRef}
          className="max-h-72 overflow-y-auto pr-1 space-y-1"
        >
          {weeks.map(function (w) {
            var isSelected = w.offset === weekOffset;
            return (
              <button
                key={w.offset}
                data-testid={'jump-picker-week-' + w.offset}
                data-current={w.isCurrent ? 'true' : 'false'}
                onClick={function () {
                  if (onSelectWeek) onSelectWeek(w.offset);
                  onClose && onClose();
                }}
                className={
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-all ' +
                  (isSelected
                    ? 'bg-accent text-white font-medium'
                    : w.isCurrent
                      ? 'bg-accent/15 text-accent font-medium'
                      : 'hover:bg-secondary')
                }
              >
                <div className="flex items-center justify-between">
                  <span>{w.label}</span>
                  {w.isCurrent && !isSelected && (
                    <span className="text-[10px] uppercase tracking-wide">This week</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-border/40 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs h-8"
            onClick={function () {
              if (onSelectWeek) onSelectWeek(0);
              onClose && onClose();
            }}
            data-testid="jump-picker-this-week"
          >
            This Week
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8"
            onClick={function () { onClose && onClose(); }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  } else {
    // month mode
    var months = buildMonthList();
    content = (
      <div data-testid="jump-picker-month">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-sm">Jump to month</span>
          <button
            onClick={function () { onClose && onClose(); }}
            className="p-1 rounded-full hover:bg-secondary"
            data-testid="jump-picker-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div
          ref={listRef}
          className="max-h-72 overflow-y-auto pr-1 space-y-1"
        >
          {months.map(function (m) {
            var isSelected = m.offset === monthOffset;
            return (
              <button
                key={m.offset}
                data-testid={'jump-picker-month-' + m.offset}
                data-current={m.isCurrent ? 'true' : 'false'}
                onClick={function () {
                  if (onSelectMonth) onSelectMonth(m.offset);
                  onClose && onClose();
                }}
                className={
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-all ' +
                  (isSelected
                    ? 'bg-accent text-white font-medium'
                    : m.isCurrent
                      ? 'bg-accent/15 text-accent font-medium'
                      : 'hover:bg-secondary')
                }
              >
                <div className="flex items-center justify-between">
                  <span>{m.label}</span>
                  {m.isCurrent && !isSelected && (
                    <span className="text-[10px] uppercase tracking-wide">This month</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-border/40 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs h-8"
            onClick={function () {
              if (onSelectMonth) onSelectMonth(0);
              onClose && onClose();
            }}
            data-testid="jump-picker-this-month"
          >
            This Month
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8"
            onClick={function () { onClose && onClose(); }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  var modal = (
    <AnimatePresence>
      <motion.div
        key="jump-picker-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
        onClick={function () { onClose && onClose(); }}
        data-testid="jump-picker-backdrop"
      >
        <motion.div
          key="jump-picker-panel"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card border border-border rounded-2xl p-4 w-full max-w-xs"
          onClick={function (e) { e.stopPropagation(); }}
        >
          {content}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Render into document.body so sticky/transformed ancestors can't trap us
  if (typeof document === 'undefined' || !document.body) {
    return modal;
  }
  return createPortal(modal, document.body);
}

export default JumpToPicker;
